'use client'

import Link from 'next/link'
import { useEffect } from 'react'
import { AlertOctagon, RotateCcw } from 'lucide-react'
import { captureException } from '@/lib/observability'

// Global error boundary. Any uncaught React render error (hydration
// mismatch, async throw inside a component, prop-type crash) would
// otherwise blow up to the browser default and show a white screen.
// Now we catch it, log it, and offer the user a way out without losing
// their navigation.

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    // Browser devtools log for local + the observability shim for prod;
    // the shim is a no-op until NEXT_PUBLIC_OBSERVABILITY_DSN or
    // NEXT_PUBLIC_SENTRY_DSN is set.
    if (typeof console !== 'undefined') {
      console.error('Uncaught error:', error, error.digest)
    }
    captureException(error, { digest: error.digest, source: 'app/error.tsx' })
  }, [error])

  return (
    <main className="min-h-screen bg-cream-100 text-ink-900 flex items-center justify-center px-6 py-16">
      <div className="max-w-md w-full">
        <div className="rounded-3xl border border-maroon-500/30 bg-cream-50 p-8 sm:p-10">
          <div className="w-12 h-12 rounded-full bg-maroon-500/12 flex items-center justify-center mb-5">
            <AlertOctagon className="w-6 h-6 text-maroon-600" />
          </div>

          <h1 className="text-2xl font-semibold tracking-[-0.02em] text-ink-900">
            Something went wrong.
          </h1>
          <p className="mt-3 text-body text-ink-700 leading-relaxed">
            We hit an unexpected error while rendering this page. The team gets a copy of the trace; nothing on your account is affected.
          </p>

          {error.digest && (
            <p className="mt-3 mono text-caption text-ink-600">
              Reference: {error.digest}
            </p>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => reset()}
              className="btn-primary px-5 py-2.5 text-sm"
            >
              Try again
              <RotateCcw className="w-4 h-4" />
            </button>
            <Link
              href="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm rounded-full border border-ink-900/15 text-ink-900 hover:bg-cream-100 transition-colors"
            >
              Back to home
            </Link>
          </div>

          <p className="mt-8 text-caption text-ink-600">
            Need help? Email{' '}
            <a
              href="mailto:hello@pulsesignal.co"
              className="text-terra-600 hover:text-terra-700 underline underline-offset-2"
            >
              hello@pulsesignal.co
            </a>
            .
          </p>
        </div>
      </div>
    </main>
  )
}
